import {Banner, CopyButton, IconButton, Link} from '@wandb/components';
import {makeDownloadUrl} from '@wandb/weave/common/util/url';
import React from 'react';

import {type ReproduceRun_RunFragment as RunInfo} from '../../generated/graphql';
import {
  useOnCopyAnalytics,
  useOnPressAnalytics,
} from '../../services/analytics';
import {ReproduceRunInfoRow, ReproduceRunInfoRows} from './ReproduceRunInfoRow';
import {ReproduceSteps} from './ReproduceSteps';
import {useRunReproduceSteps} from './useRunReproduceSteps';

type ReproduceRunProps = {
  // Assuming that this component is in the context of a drawer or modal, this
  // allows it to close itself when the user clicks the "view" button on the
  // patch file.
  onClose?(): void;
  run: RunInfo;
};

export function ReproduceRun({onClose, run}: ReproduceRunProps) {
  const {
    remote,
    commit,
    patch,
    patchViewUrl,
    executable,
    setupSteps,
    executeSteps,
  } = useRunReproduceSteps(run);

  const onViewPatch = useOnPressAnalytics(
    'Reproduce run',
    {action: 'view patch'},
    onClose
  );
  const onCopyRepository = useOnCopyAnalytics('Reproduce run', {
    action: 'copy property',
    property: 'repository',
  });
  const onCopyCommit = useOnCopyAnalytics('Reproduce run', {
    action: 'copy property',
    property: 'commit',
  });
  const onCopyPythonExec = useOnCopyAnalytics('Reproduce run', {
    action: 'copy property',
    property: 'python executable',
  });
  const onDownloadPatch = useOnPressAnalytics('Reproduce run', {
    action: 'download patch',
  });

  return (
    <div className="flex flex-col gap-16">
      <ReproduceRunInfoRows>
        {remote ? (
          <ReproduceRunInfoRow
            content={remote}
            controls={<CopyButton content={remote} onCopy={onCopyRepository} />}
            label="Repository"
          />
        ) : (
          <ReproduceRunInfoRow content="-" label="Repository" />
        )}
        {commit ? (
          <ReproduceRunInfoRow
            content={commit}
            controls={<CopyButton content={commit} onCopy={onCopyCommit} />}
            label="Commit"
          />
        ) : (
          <ReproduceRunInfoRow content="-" label="Commit" />
        )}
        {patch ? (
          <ReproduceRunInfoRow
            content={patch.name}
            controls={
              <>
                {patchViewUrl ? (
                  <Link href={patchViewUrl} onPress={onViewPatch}>
                    View
                  </Link>
                ) : null}
                {patch.url ? (
                  <IconButton
                    aria-label="download"
                    icon="download"
                    variant="ghost"
                    href={makeDownloadUrl(patch.url, patch.name)}
                    download={patch.name}
                    onPress={onDownloadPatch}
                  />
                ) : null}
              </>
            }
            label="Patch"
          />
        ) : null}
        {executable ? (
          <ReproduceRunInfoRow
            content={executable}
            label="Python executable"
            controls={
              <CopyButton content={executable} onCopy={onCopyPythonExec} />
            }
          />
        ) : null}
      </ReproduceRunInfoRows>

      {setupSteps ? (
        <ReproduceSteps steps={setupSteps} title="Set up code state" />
      ) : (
        <Banner isDismissible={false} variant="warning">
          <p>
            This run does not have all the necessary information to reproduce
            it. This could be because the git respository does not have an{' '}
            <code className="text-[13px] font-semibold">origin</code> that it is
            tracked against, or which can be cloned with git.
          </p>
        </Banner>
      )}
      {executeSteps ? (
        <ReproduceSteps steps={executeSteps} title="Run command" />
      ) : null}
    </div>
  );
}
